import { useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { audioEngine } from '@/audio/AudioEngine';
import { FREQUENCY_GLOW_COLORS } from '@/types';

interface HUDProps {
  hintAvailable?: boolean;
  hintLevel?: number;
  onRequestHint?: () => void;
}

export function HUD({ hintAvailable = false, hintLevel = 0, onRequestHint }: HUDProps) {
  const strikesUsed = useGameStore((s) => s.strikesUsed);
  const strikeBudget = useGameStore((s) => s.strikeBudget);
  const undoAvailable = useGameStore((s) => s.undoAvailable);
  const isComplete = useGameStore((s) => s.isComplete);
  const harmonyReport = useGameStore((s) => s.harmonyReport);
  const undo = useGameStore((s) => s.undo);
  const reset = useGameStore((s) => s.reset);

  const [isMuted, setIsMuted] = useState(audioEngine.isMuted());

  const handleToggleMute = () => {
    audioEngine.toggleMute();
    setIsMuted(audioEngine.isMuted());
  };

  const resonance = harmonyReport ? harmonyReport.resonancePercentage : 0;
  const nearComplete = resonance >= 80;

  // Build strike dots as small colored orbs
  const strikeDots: React.ReactNode[] = [];
  // Cycle through a few glow colors for visual variety
  const orbColorKeys = [1, 9, 6, 3, 11, 7, 5, 10, 8, 2, 4, 12];
  for (let i = 0; i < strikeBudget; i++) {
    const used = i < strikesUsed;
    const colorKey = orbColorKeys[i % orbColorKeys.length] ?? 1;
    const orbColor = FREQUENCY_GLOW_COLORS[colorKey] ?? '#888';
    strikeDots.push(
      <span
        key={i}
        style={{
          display: 'inline-block',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: used
            ? `radial-gradient(circle at 40% 35%, ${lightenHex(orbColor, 40)}, ${orbColor})`
            : 'rgba(255,255,255,0.08)',
          boxShadow: used
            ? `0 0 6px ${hexToRgba(orbColor, 0.5)}, 0 0 2px ${hexToRgba(orbColor, 0.3)}`
            : 'none',
          border: used
            ? `1px solid ${hexToRgba(orbColor, 0.4)}`
            : '1px solid rgba(255,255,255,0.08)',
          transition: 'all 0.3s ease',
        }}
      />,
    );
  }

  // Resonance bar gradient color
  const barGradient = getResonanceGradient(resonance);

  // Compute dynamic glow style for resonance bar when near 100%
  const barFillStyle: React.CSSProperties = {
    ...progressBarFillStyle,
    width: `${resonance}%`,
    background: barGradient,
    boxShadow: nearComplete
      ? '0 0 10px rgba(81, 207, 102, 0.5), 0 0 20px rgba(81, 207, 102, 0.2)'
      : '0 0 6px rgba(81, 207, 102, 0.15)',
  };

  if (nearComplete) {
    barFillStyle.animation = 'resonanceGlow 2s ease-in-out infinite';
  }

  const showHintButton = hintAvailable && hintLevel < 2 && !isComplete;

  return (
    <div style={hudContainerStyle}>
      {/* Resonance progress bar */}
      <div style={progressRowStyle}>
        <span style={progressLabelStyle}>Resonance</span>
        <div
          style={progressBarTrackStyle}
          role="progressbar"
          aria-valuenow={resonance}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Resonance: ${resonance}%`}
        >
          <div style={barFillStyle} />
        </div>
        <span style={{
          ...progressValueStyle,
          color: nearComplete ? '#51CF66' : '#e8e8e4',
          textShadow: nearComplete ? '0 0 8px rgba(81, 207, 102, 0.4)' : 'none',
        }}>
          {resonance}%
        </span>
      </div>

      {/* Bottom row: strikes, actions, status */}
      <div style={bottomRowStyle}>
        {/* Strike counter */}
        <div style={strikeContainerStyle}>
          <span style={strikeLabelStyle}>Strikes</span>
          <div style={strikeDotsStyle} aria-label={`${strikesUsed} of ${strikeBudget} strikes used`}>
            {strikeDots}
          </div>
        </div>

        {/* Action buttons */}
        <div style={actionsStyle}>
          <button
            style={undoAvailable && !isComplete ? buttonStyle : disabledButtonStyle}
            disabled={!undoAvailable || isComplete}
            onClick={undo}
            aria-label="Undo last strike"
          >
            Undo
          </button>
          <button
            style={buttonStyle}
            onClick={reset}
            aria-label="Reset puzzle"
          >
            Reset
          </button>
          {showHintButton && (
            <button
              style={hintButtonStyle}
              onClick={onRequestHint}
              aria-label="Request hint"
              title="Request a hint"
            >
              ?
            </button>
          )}
          <button
            style={muteButtonStyle}
            onClick={handleToggleMute}
            aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '\uD83D\uDD07' : '\uD83D\uDD0A'}
          </button>
        </div>
      </div>
    </div>
  );
}

function getResonanceGradient(percent: number): string {
  if (percent <= 0) return '#E63946';
  if (percent >= 100) return 'linear-gradient(90deg, #E63946 0%, #FCBF49 40%, #51CF66 100%)';
  return 'linear-gradient(90deg, #E63946 0%, #FCBF49 50%, #51CF66 100%)';
}

/** Lighten a hex color by a percentage. */
function lightenHex(hex: string, percent: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const factor = percent / 100;
  const nr = Math.min(255, Math.round(r + (255 - r) * factor));
  const ng = Math.min(255, Math.round(g + (255 - g) * factor));
  const nb = Math.min(255, Math.round(b + (255 - b) * factor));
  const toH = (v: number) => v.toString(16).padStart(2, '0');
  return `#${toH(nr)}${toH(ng)}${toH(nb)}`;
}

/** Convert hex to rgba string. */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Styles ──

const hudContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '10px 16px 14px',
  background: 'rgba(10, 10, 30, 0.75)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderTop: '1px solid rgba(255,255,255,0.04)',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  userSelect: 'none',
  flexShrink: 0,
};

const progressRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const progressLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: 'rgba(232,232,228,0.45)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  flexShrink: 0,
  width: '72px',
};

const progressBarTrackStyle: React.CSSProperties = {
  flex: 1,
  height: '6px',
  background: 'rgba(255,255,255,0.04)',
  borderRadius: '3px',
  overflow: 'hidden',
  position: 'relative',
};

const progressBarFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: '3px',
  transition: 'width 0.4s ease-out, box-shadow 0.4s ease-out',
};

const progressValueStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#e8e8e4',
  fontVariantNumeric: 'tabular-nums',
  width: '36px',
  textAlign: 'right',
  flexShrink: 0,
  transition: 'color 0.3s ease, text-shadow 0.3s ease',
};

const bottomRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const strikeContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const strikeLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: 'rgba(232,232,228,0.45)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const strikeDotsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '5px',
  alignItems: 'center',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const buttonStyle: React.CSSProperties = {
  padding: '6px 16px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '6px',
  color: '#e8e8e4',
  fontSize: '12px',
  fontWeight: 400,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  letterSpacing: '0.03em',
  outline: 'none',
};

const disabledButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  opacity: 0.3,
  cursor: 'not-allowed',
};

const hintButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  padding: '6px 12px',
  background: 'rgba(252, 191, 73, 0.1)',
  border: '1px solid rgba(252, 191, 73, 0.25)',
  color: '#FCBF49',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: 1,
};

const muteButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  padding: '6px 10px',
  fontSize: '16px',
  lineHeight: 1,
};
